import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

export default class extends Controller {
  static targets = [ "input", "submit" ]
  static values = { requestId: String, draftUrl: String }

  connect() {
    this.files = []
    this.hiddenInput = document.getElementById("hidden-input")
    this.gallery = document.getElementById("attachments")
    this.cancelButton = document.getElementById("cancel")
    this.fileTempl = document.getElementById("file-template")
    this.errorMessage = document.getElementById("upload-limits")

    if (document.getElementById("button") !== null) {
      document.getElementById("button").onclick = (ev) => {
        ev.preventDefault();
        this.hiddenInput.click();
      }
    }

    document.documentElement.style.setProperty("--chat-form-height", "90px")

    this.restoreDraft()
    this.inputTarget.focus()

    this.morphHandler = () => this.restoreDraft()
    document.addEventListener("turbo:morph", this.morphHandler)
  }

  get hasValidRequestId() {
    return this.hasRequestIdValue && typeof this.requestIdValue === "string" && /^[0-9a-f-]{36}$/i.test(this.requestIdValue)
  }

  get draftKey() {
    return `draft-message-${this.requestIdValue}`
  }

  restoreDraft() {
    if (!this.hasValidRequestId) return
    if (this.inputTarget.value.trim() !== "") return

    try {
      const raw = localStorage.getItem(this.draftKey)
      if (!raw) return

      const draft = JSON.parse(raw)
      const oneDay = 24 * 60 * 60 * 1000
      if (Date.now() - draft.savedAt > oneDay) {
        localStorage.removeItem(this.draftKey)
        return
      }

      this.inputTarget.value = draft.text
      this.enable()
    } catch {
      localStorage.removeItem(this.draftKey)
    }
  }

  saveDraft() {
    if (this.hasDraftUrlValue) {
      this.syncDraftToServer()
    }
    if (this.hasValidRequestId) {
      this.saveLocalDraft()
    }
  }

  syncDraftToServer() {
    clearTimeout(this.draftTimer)

    if (!this.hasSyncedDraft) {
      this.hasSyncedDraft = true
      this.sendDraftToServer()
    } else {
      this.draftTimer = setTimeout(() => this.sendDraftToServer(), 2000)
    }
  }

  sendDraftToServer() {
    patch(this.draftUrlValue, {
      body: JSON.stringify({ draft_text: this.inputTarget.value })
    })
  }

  saveLocalDraft() {
    try {
      const text = this.inputTarget.value
      if (text.trim() === "") {
        localStorage.removeItem(this.draftKey)
      } else {
        localStorage.setItem(this.draftKey, JSON.stringify({
          text,
          savedAt: Date.now()
        }))
      }
    } catch {
      // localStorage unavailable or full
    }
  }

  clear() {
    this.inputTarget.value = ""
    if (this.hasDraftUrlValue) {
      clearTimeout(this.draftTimer)
      this.hasSyncedDraft = false
      this.sendDraftToServer()
    }
    if (this.hasValidRequestId) {
      try { localStorage.removeItem(this.draftKey) } catch {}
    }

    var event = new KeyboardEvent('keydown', {
      key: 'Backspace',
      code: 'Backspace',
      keyCode: 8,
      which: 8,
    })

    this.inputTarget.dispatchEvent(event)
    this.cancelButton.classList.add("hidden")

    this.cancel()
  }
  
  showFinishButton() {
    if (document.body.dataset.currentRole === "agent") {
      const serviceRequestId = new FormData(this.element).get("service_request_id")
      if (serviceRequestId !== null) {
        document.getElementById(`finish_button_service_request_${serviceRequestId}`).classList.remove("hidden")
      }
    }
  }

  cancel(event) {
    if (event) {
      event.preventDefault()
    }

    let hiddenAttachments = document.querySelectorAll("[name='message[attachments][]'][value]")
    hiddenAttachments.forEach((e) => {
      e.remove()
    })

    if (this.cancelButton !== null) {
      this.cancelButton.classList.add("hidden")
    }
    this.hiddenInput.value = null
    this.hiddenInput.removeAttribute("disabled")

    this.gallery.querySelectorAll('div.attachment').forEach((e) => {
      e.remove()
    })

    this.files = {}

    this.gallery.classList.add("hidden")

    document.documentElement.style.setProperty("--chat-form-height", "90px")

    this.enable()
  }

  attachmentsChange(e) {
    let valid = true;
    for (const file of e.target.files) {
      valid = valid && this.addFile(this.gallery, file)
    }

    if (valid) {
      this.enable()
    }
    else {
      e.target.value = null
      this.cancel()
    }
  }

  addFile(target, file) {
    this.errorMessage.classList.add("hidden")
    const maxAllowedSize = 50 * 1024 * 1024;
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "application/pdf",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain"
    ]

    if (file.size > maxAllowedSize || !allowedTypes.includes(file.type)) {
      this.errorMessage.classList.remove("hidden")
      return;
    }

    if (this.cancelButton !== null) {
      this.cancelButton.classList.remove("hidden")
    }

    const isImage = file.type.match("image.*"), objectURL = URL.createObjectURL(file)
    const clone = this.fileTempl.content.cloneNode(true)

    clone.querySelector(".name").textContent = file.name
    isImage &&
      Object.assign(clone.querySelector("img"), {
        src: objectURL,
        alt: file.name
      });

    target.prepend(clone)

    this.gallery.classList.remove("hidden")

    this.files[objectURL] = file;

    document.documentElement.style.setProperty("--chat-form-height", "222px")
    return true
  }

  enable() {
    let attachedFiles = 0
    this.gallery.querySelectorAll('div.attachment').forEach((e) => {
      attachedFiles += 1
    })

    const regex = /\S/
    if (regex.test(this.inputTarget.value) === false && attachedFiles === 0) {
      this.submitTarget.disabled = true
    } else {
      this.submitTarget.disabled = false
    }
  }

  keydown(event) {
    if (event.which == 13 && this.inputTarget.value.length === 0) {
      event.preventDefault()
      return
    }

    if (event.which == 13 && !event.shiftKey) {
      this.element.requestSubmit()
      this.cancelButton.classList.add("hidden")
    }
  }

  disconnect() {
    clearTimeout(this.draftTimer)
    document.removeEventListener("turbo:morph", this.morphHandler)
  }

}
